import { useEffect, useRef, type RefObject } from 'react'

/**
 * Volta a lista ao topo quando ela troca (canal, página, filtro) e termina de
 * carregar. Se a pessoa rolou enquanto carregava, fica onde ela está; se rolar
 * durante a animação, a animação para.
 *
 * A troca é a mudança de ``listKey``, não o carregamento: lista que já está no
 * cache chega sem passar por "carregando", e a página ficava parada no rodapé
 * da anterior.
 *
 * Quem rola é o ``.content`` do AppLayout, não a janela: o alvo é o primeiro
 * ancestral rolável do ``anchor``.
 */

/** Teclas que rolam a página; as outras (atalhos, digitação) não contam. */
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '])

/** Onde as setas e o espaço mexem num controle, não na página. */
const CONTROL_SELECTOR = 'input, textarea, select, [contenteditable], [role="radio"], [role="tab"], [role="option"], [role="slider"]'

export function isScrollKey(key: string): boolean {
  return SCROLL_KEYS.has(key)
}

/** Rolar para o topo só se a pessoa não rolou e se há para onde subir. */
export function shouldScrollToTop(userScrolled: boolean, scrollTop: number): boolean {
  return !userScrolled && scrollTop > 0
}

function scrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node
  }
  return (document.scrollingElement as HTMLElement | null) ?? null
}

/** Rolagem iniciada pela pessoa: roda, toque, tecla de rolagem ou a barra de rolagem. */
function onUserScroll(scroller: HTMLElement, handler: () => void): () => void {
  const onKey = (e: KeyboardEvent) => {
    if (!isScrollKey(e.key)) return
    if (e.target instanceof Element && e.target.closest(CONTROL_SELECTOR)) return
    handler()
  }
  // Clicar na barra de rolagem chega como pointerdown no próprio scroller.
  const onPointer = (e: PointerEvent) => {
    if (e.target === scroller) handler()
  }
  scroller.addEventListener('wheel', handler, { passive: true })
  scroller.addEventListener('touchmove', handler, { passive: true })
  scroller.addEventListener('pointerdown', onPointer)
  window.addEventListener('keydown', onKey)
  return () => {
    scroller.removeEventListener('wheel', handler)
    scroller.removeEventListener('touchmove', handler)
    scroller.removeEventListener('pointerdown', onPointer)
    window.removeEventListener('keydown', onKey)
  }
}

export function useScrollToTopAfterLoad(
  anchor: RefObject<HTMLElement | null>,
  listKey: string,
  loading: boolean,
): void {
  const userScrolled = useRef(false)
  const lastKey = useRef(listKey)
  // Uma troca esperando a lista chegar para voltar ao topo.
  const pending = useRef(false)

  useEffect(() => {
    const scroller = scrollParent(anchor.current)
    if (!scroller) return

    if (listKey !== lastKey.current) {
      lastKey.current = listKey
      pending.current = true
      // O clique que trocou a lista não conta: só o que vier depois dele.
      userScrolled.current = false
    }
    if (!pending.current) return
    if (loading) {
      return onUserScroll(scroller, () => {
        userScrolled.current = true
      })
    }
    pending.current = false
    if (!shouldScrollToTop(userScrolled.current, scroller.scrollTop)) return

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    scroller.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })
    if (reduce) return
    // Rolar durante a animação a interrompe: o navegador para a rolagem suave
    // quando recebe outra para o ponto onde a página está.
    let stopListening = (): void => undefined
    const stop = () => {
      stopListening()
      scroller.scrollTo({ top: scroller.scrollTop })
    }
    stopListening = onUserScroll(scroller, stop)
    const timer = window.setTimeout(stopListening, 1500)
    return () => {
      window.clearTimeout(timer)
      stopListening()
    }
  }, [anchor, listKey, loading])
}
